/**
 * Ask Cannafax names buyer companies and listings as markdown links:
 *   [Name](/cfx/company/<uuid>)  → contact card
 *   [Name](/listings/<uuid>)     → listing preview
 *   [Name](/cfx/listing-card/<uuid>) → full listing card (photo + specs) in the chat
 *   [Label](/cfx/share/<listing-uuid>?to=<company-uuid>) → creates a tracked share link and opens a pre-filled draft (user sends)
 *   [Label](/cfx/compose/<uuid>?draft=…) → Messages composer, pre-filled (user sends)
 * Matched anywhere in the href so a sanitiser that prefixes an origin still resolves.
 */
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const COMPANY = new RegExp(`/cfx/company/(${UUID})(?:$|[/?#])`, 'i');
const LISTING = new RegExp(`/listings/(${UUID})(?:$|[?#])`, 'i');
const LISTING_CARD = new RegExp(`/cfx/listing-card/(${UUID})(?:$|[/?#])`, 'i');
const SHARE = new RegExp(`/cfx/share/(${UUID})\\?(?:[^#]*&)?to=(${UUID})`, 'i');
const MENU_SHARE = new RegExp(`/cfx/share-menu/(${UUID})\\?to=(${UUID})$`, 'i');
const MENU_SHARE_OPEN = new RegExp(`/cfx/share-menu/(${UUID})$`, 'i');
const COMPOSE = new RegExp(`/cfx/compose/(${UUID})(?:\\?([^#]*))?`, 'i');

export type EntityRef =
  | { kind: 'company' | 'listing' | 'listingCard'; id: string }
  | { kind: 'compose'; id: string; draft: string }
  | { kind: 'share'; listingId: string; companyId: string }
  | { kind: 'menuShare'; menuId: string; companyId: string }
  | { kind: 'menuShareOpen'; menuId: string };

/** Where a prepared draft opens: the Messages composer, recipient chosen and text filled, never sent. */
export function composeRoute(companyId: string, draft: string): string {
  const q = new URLSearchParams({ to: companyId });
  if (draft) q.set('draft', draft);
  return `/messages?${q.toString()}`;
}

/** The note that accompanies a listing shared from the chat; the user edits it before sending. */
export function shareDraft(listingName: string, url: string): string {
  return `Thought you'd like this one — ${listingName}. Details and photos here: ${url}`;
}

export function parseEntityHref(href: string | null | undefined): EntityRef | null {
  if (!href) return null;
  const ms = href.match(MENU_SHARE);
  if (ms) return { kind: 'menuShare', menuId: ms[1].toLowerCase(), companyId: ms[2].toLowerCase() };
  const mo = href.match(MENU_SHARE_OPEN);
  if (mo) return { kind: 'menuShareOpen', menuId: mo[1].toLowerCase() };
  const m = href.match(COMPOSE);
  if (m) {
    let draft = '';
    try { draft = new URLSearchParams(m[2] ?? '').get('draft') ?? ''; } catch { draft = ''; }
    return { kind: 'compose', id: m[1].toLowerCase(), draft: draft.slice(0, 4000) };
  }
  const s = href.match(SHARE);
  if (s) return { kind: 'share', listingId: s[1].toLowerCase(), companyId: s[2].toLowerCase() };
  const lc = href.match(LISTING_CARD);
  if (lc) return { kind: 'listingCard', id: lc[1].toLowerCase() };
  const c = href.match(COMPANY);
  if (c) return { kind: 'company', id: c[1].toLowerCase() };
  const l = href.match(LISTING);
  if (l) return { kind: 'listing', id: l[1].toLowerCase() };
  return null;
}

// App-internal paths the assistant must never show as text (actions render as buttons).
const BARE_ROUTE = /`?(?:https?:\/\/[^\s)`]+)?\/(?:cfx\/[^\s)`]+|messages\?[^\s)`]+|listings\/[0-9a-f-]{36}[^\s)`]*|my-menu\/[^\s)`]+)`?/gi;
const LEAD_IN = /^\s*(?:[-*]\s+|\d+\.\s+)?(?:\*\*)?(?:act here|link|go to|open here|action|url)(?:\*\*)?\s*[:\-–—]?\s*(?:\*\*)?\s*$/i;

/**
 * Universal guard for every assistant answer: markdown links stay (they become
 * buttons/cards), but any internal route leaked as plain text is removed, along
 * with the "Act here:" lead-in left behind and duplicate actions.
 */
export function cleanAssistantMarkdown(text: string): string {
  if (!text) return text;
  const links: string[] = [];
  const seen = new Set<string>();
  // Protect real markdown links; drop exact duplicate actions.
  let out = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label: string, href: string) => {
    const key = parseEntityHref(href) ? href.toLowerCase() : '';
    if (key && seen.has(key)) return '';
    if (key) seen.add(key);
    links.push(`[${label}](${href})`);
    return `\u0000${links.length - 1}\u0000`;
  });
  out = out.replace(BARE_ROUTE, '');
  out = out
    .split('\n')
    .map((l) => l.replace(/\s+$/, ''))
    .filter((l) => !LEAD_IN.test(l) && !/^\s*(?:[-*]|\d+\.)\s*$/.test(l))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n');
  return out.replace(/\u0000(\d+)\u0000/g, (_m, i) => links[Number(i)]);
}
